import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Zap, CheckCircle2, XCircle, ArrowLeft, Trophy, RotateCcw, Shuffle, Leaf, GlassWater, Dices, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const C = {
  cardBg: "#111111",
  headerBg: "#0d0d0d",
  copper: "#dcaa2c",
  copperLight: "rgba(220,170,44,0.2)",
  textMuted: "#888",
  textLight: "#bbb",
  pageBg: "#000",
};

const CHALLENGES = [
  { id: "random20", label: "Random 20", desc: "Mixed measurement, brand, garnish & glassware questions pulled at random from this deck", icon: Shuffle, maxCount: 20, estimate: "~3–5 min" },
  { id: "garnish-glass", label: "Garnish & Glassware", desc: "Only garnish and glassware questions — no measurements or brands", icon: GlassWater, maxCount: 20, estimate: "~3–5 min" },
  { id: "match-garnish", label: "Match the Garnish", desc: "Match each cocktail in this deck to its correct garnish", icon: Leaf, maxCount: 10, estimate: "~1–2 min" },
  { id: "random-cocktail", label: "Random Cocktail", desc: "1 random cocktail from this deck — every one of its ingredient, garnish & glassware questions", icon: Dices, maxCount: null, estimate: null },
];

/* Brief, data-driven explanation of the correct answer — built only from the
   fields already used to grade the question (never invents recipe data). */
const explainAnswer = (q) => {
  const name = q.ingredient_name;
  switch (q.type) {
    case "measurement":
      return `${name || "That ingredient"} in the ${q.cocktail_name} uses ${q.correct_answer}.`;
    case "brand":
      return `The ${name || "ingredient"} in the ${q.cocktail_name} is ${q.correct_answer}.`;
    case "garnish":
      return `${q.cocktail_name} is garnished with ${q.correct_answer}.`;
    case "glassware":
      return `${q.cocktail_name} is served in a ${q.correct_answer}.`;
    default:
      return `Correct answer: ${q.correct_answer}.`;
  }
};

/* Shared exit-confirm — practice sessions are never saved until fully
   completed, so leaving early simply discards progress with zero XP. Making
   that explicit (rather than silently wiping the session) is the "clear way
   to exit" this phase asks for. */
function ExitConfirmDialog({ onStay, onLeave }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onStay(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onStay]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onStay} data-testid="exit-confirm-overlay">
      <div className="max-w-xs w-full rounded-xl border p-5 space-y-4 text-center" style={{ borderColor: C.copper, background: C.cardBg }} onClick={(e) => e.stopPropagation()}>
        <p className="text-sm font-bold text-white">Leave this practice session?</p>
        <p className="text-xs" style={{ color: C.textMuted }}>Progress isn't saved until you finish — leaving now earns 0 XP for this attempt.</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onStay} className="flex-1 border-[#333] text-white hover:bg-white/5" data-testid="exit-confirm-stay">Stay</Button>
          <Button onClick={onLeave} className="flex-1" style={{ background: "#ef4444", color: "#fff" }} data-testid="exit-confirm-leave">Leave</Button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   SHARED: Results screen
   ═══════════════════════════════════════════════════ */
function ResultsScreen({ correct, wrong, total, deckName, onRestart, onBack, xpData }) {
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.copper, background: C.cardBg }}>
        <div className="px-4 py-3 text-center" style={{ background: C.headerBg }}>
          <span className="text-sm font-bold uppercase tracking-widest" style={{ color: C.copper }}>Practice Complete</span>
        </div>
        <div className="p-6 space-y-6 text-center">
          <Trophy size={48} className="mx-auto" style={{ color: C.copper }} />
          <div>
            <p className="text-4xl font-bold text-white" data-testid="challenge-final-score">{pct}%</p>
            <p className="text-sm mt-1" style={{ color: C.textMuted }}>{deckName}</p>
            <p className="text-[10px] uppercase tracking-wide mt-1" style={{ color: C.copper }}>XP-scored practice — not an official Test, and doesn't change any card's Mastered status</p>
          </div>
          <div className="flex justify-center gap-8">
            <div className="text-center"><p className="text-2xl font-bold text-green-400" data-testid="challenge-correct-count">{correct}</p><p className="text-xs" style={{ color: C.textMuted }}>Correct</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-red-400" data-testid="challenge-wrong-count">{wrong}</p><p className="text-xs" style={{ color: C.textMuted }}>Wrong</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-white">{total}</p><p className="text-xs" style={{ color: C.textMuted }}>Total</p></div>
          </div>

          {/* XP Reward */}
          {xpData && (
            <div className="rounded-lg border p-4 space-y-2" style={{ borderColor: C.copperLight, background: "rgba(220,170,44,0.05)" }} data-testid="xp-reward">
              <p className="text-lg font-bold" style={{ color: C.copper }}>+{xpData.xp_earned} XP</p>
              <p className="text-xs" style={{ color: C.textMuted }}>
                Level {xpData.level?.level} {xpData.level?.title} — {xpData.total_xp} XP total
              </p>
              {xpData.new_badges?.length > 0 && (
                <div className="pt-2 space-y-1">
                  {xpData.new_badges.map((b, i) => (
                    <div key={i} className="flex items-center justify-center gap-2 text-sm font-bold text-[#C36A2F]">
                      <Award size={16} /> Badge Unlocked: {b.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button onClick={onRestart} className="flex-1" style={{ background: C.copper, color: "#000" }} data-testid="challenge-restart"><RotateCcw size={16} className="mr-2" /> Try Again</Button>
            <Button variant="outline" onClick={onBack} className="flex-1 border-[#333] text-white hover:bg-white/5" data-testid="challenge-back-done"><ArrowLeft size={16} className="mr-2" /> Back</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   QUIZ MODE: Multiple choice (random20, garnish-glass, random-cocktail)
   ═══════════════════════════════════════════════════ */
function QuizMode({ questions, deckName, subtitle, challengeType, onRestart, onBack }) {
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [finished, setFinished] = useState(false);
  const [xpData, setXpData] = useState(null);
  const [startTime] = useState(Date.now());
  const [confirmExit, setConfirmExit] = useState(false);
  const submittingRef = useRef(false);

  const q = questions[current];
  const total = questions.length;
  const progress = total > 0 ? ((current + (showFeedback ? 1 : 0)) / total) * 100 : 0;

  const handleAnswer = (option) => {
    if (showFeedback) return; // a question can only be answered once per session
    setSelected(option);
    setShowFeedback(true);
    if (option === q.correct_answer) setCorrect(c => c + 1);
    else setWrong(w => w + 1);
  };

  const handleNext = () => {
    if (current + 1 >= total) {
      if (submittingRef.current) return; // guards a rapid double-click from firing two XP awards
      submittingRef.current = true;
      const duration = Math.round((Date.now() - startTime) / 1000);
      axios.post(`${API}/xp/award-challenge`, {
        challenge_type: challengeType, correct: correct, total, deck_name: deckName, duration_seconds: duration,
      }).then(res => setXpData(res.data)).catch(() => {});
      setFinished(true);
      return;
    }
    setCurrent(c => c + 1);
    setSelected(null);
    setShowFeedback(false);
  };

  // Leaving mid-question/mid-session discards it with 0 XP (nothing is saved
  // until the final question is answered) — confirm first once there's
  // actually something to lose.
  const requestExit = () => {
    if (current > 0 || showFeedback) setConfirmExit(true);
    else onBack();
  };

  if (finished) return <ResultsScreen correct={correct} wrong={wrong} total={total} deckName={deckName} onRestart={onRestart} onBack={onBack} xpData={xpData} />;

  return (
    <div className="max-w-lg mx-auto space-y-4">
      {confirmExit && <ExitConfirmDialog onStay={() => setConfirmExit(false)} onLeave={onBack} />}
      <div className="flex items-center justify-between gap-2">
        <button onClick={requestExit} className="flex items-center gap-1 text-sm shrink-0 py-2 -my-2" style={{ color: C.textMuted }} data-testid="challenge-back"><ArrowLeft size={16} /> Exit</button>
        <span className="text-xs shrink-0" style={{ color: C.textMuted }} data-testid="challenge-progress">Question {current + 1} of {total}</span>
      </div>
      <div className="flex items-center justify-center gap-2">
        <Zap size={16} style={{ color: C.copper }} /><span className="text-sm font-bold" style={{ color: C.copper }}>{subtitle || "Practice"}</span>
      </div>
      <Progress value={progress} className="h-1.5 bg-[#222]" />
      <div className="flex justify-center gap-6 text-xs">
        <span className="flex items-center gap-1 text-green-400"><CheckCircle2 size={14} /> {correct}</span>
        <span className="flex items-center gap-1 text-red-400"><XCircle size={14} /> {wrong}</span>
      </div>
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: "#333", background: C.cardBg }} data-testid={`challenge-question-${current}`}>
        <div className="px-4 py-3 border-b" style={{ borderColor: "#222", background: C.headerBg }}>
          <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.copper }}>{q.type}</p>
          <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{q.cocktail_name}</p>
        </div>
        <div className="p-4">
          <p className="text-white text-sm font-medium mb-4" style={{ fontFamily: "Georgia, serif" }}>{q.question_text}</p>
          <div className="space-y-2">
            {q.options.map((opt, i) => {
              let bg = "rgba(255,255,255,0.05)", border = "#333", textColor = "#fff", icon = null;
              if (showFeedback) {
                if (opt === q.correct_answer) { bg = "rgba(34,197,94,0.15)"; border = "#22c55e"; textColor = "#22c55e"; icon = <CheckCircle2 size={16} className="shrink-0" />; }
                else if (opt === selected && opt !== q.correct_answer) { bg = "rgba(239,68,68,0.15)"; border = "#ef4444"; textColor = "#ef4444"; icon = <XCircle size={16} className="shrink-0" />; }
              } else if (opt === selected) { bg = C.copperLight; border = C.copper; }
              return (
                <button key={i} onClick={() => handleAnswer(opt)} disabled={showFeedback}
                  className="w-full flex items-center justify-between gap-2 text-left px-4 py-3 rounded-lg border text-sm transition-all min-h-[48px]"
                  style={{ background: bg, borderColor: border, color: textColor }} data-testid={`challenge-option-${i}`}
                >
                  <span>{opt}</span>{icon}
                </button>
              );
            })}
          </div>
        </div>
        {showFeedback && (
          <div className="px-4 pb-4">
            <div className="flex items-center gap-2 mb-2">
              {selected === q.correct_answer
                ? <><CheckCircle2 size={18} className="text-green-400" /><span className="text-green-400 text-sm font-bold">Correct!</span></>
                : <><XCircle size={18} className="text-red-400" /><span className="text-red-400 text-sm font-bold">Wrong</span></>}
            </div>
            <p className="text-xs mb-3" style={{ color: C.textLight }} data-testid="challenge-explanation">{explainAnswer(q)}</p>
            <Button onClick={handleNext} className="w-full h-12 font-bold" style={{ background: C.copper, color: "#000" }} data-testid="challenge-next">
              {current + 1 >= total ? "See Results" : "Next Question"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MATCH MODE: Drag & drop garnish matching
   ═══════════════════════════════════════════════════ */
function MatchMode({ pairs, allGarnishes, deckName, onRestart, onBack }) {
  const [assignments, setAssignments] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [xpData, setXpData] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [touchDragging, setTouchDragging] = useState(null);
  const [confirmExit, setConfirmExit] = useState(false);
  const [startTime] = useState(Date.now());
  const submittingRef = useRef(false);
  const containerRef = useRef(null);

  // Build garnish pool: one chip per cocktail (allows duplicates since multiple cocktails can share a garnish)
  const [shuffledGarnishes] = useState(() => {
    const g = pairs.map(p => p.garnish);
    for (let i = g.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [g[i], g[j]] = [g[j], g[i]]; }
    return g.map((name, idx) => ({ id: `${name}-${idx}`, name }));
  });

  const assignedValues = Object.values(assignments);
  const availableGarnishes = shuffledGarnishes.filter(g => !assignedValues.includes(g.id));

  const handleDragStart = (garnishObj) => setDragging(garnishObj);
  const handleDrop = (cocktailId) => {
    if (dragging) { setAssignments(prev => ({ ...prev, [cocktailId]: dragging.id })); setDragging(null); }
  };

  const handleTouchStart = (garnishObj) => setTouchDragging(garnishObj);
  const handleTouchDrop = (cocktailId) => {
    if (touchDragging) { setAssignments(prev => ({ ...prev, [cocktailId]: touchDragging.id })); setTouchDragging(null); }
  };

  const removeAssignment = (cocktailId) => {
    if (submitted) return;
    setAssignments(prev => { const n = { ...prev }; delete n[cocktailId]; return n; });
  };

  const handleSubmit = () => {
    if (submittingRef.current) return; // guards a rapid double-click from firing two XP awards
    submittingRef.current = true;
    const cc = pairs.filter(p => getGarnishName(assignments[p.cocktail_id]) === p.garnish).length;
    const duration = Math.round((Date.now() - startTime) / 1000);
    axios.post(`${API}/xp/award-challenge`, {
      challenge_type: "match-garnish", correct: cc, total: pairs.length, deck_name: deckName, duration_seconds: duration,
    }).then(res => setXpData(res.data)).catch(() => {});
    setSubmitted(true);
  };

  // Resolve assigned garnish ID back to name for display and scoring
  const getGarnishName = (gId) => shuffledGarnishes.find(g => g.id === gId)?.name || "";
  const correctCount = pairs.filter(p => getGarnishName(assignments[p.cocktail_id]) === p.garnish).length;
  const total = pairs.length;
  const allAssigned = Object.keys(assignments).length === total;

  const requestExit = () => {
    if (Object.keys(assignments).length > 0) setConfirmExit(true);
    else onBack();
  };

  if (submitted && reviewed) return <ResultsScreen correct={correctCount} wrong={total - correctCount} total={total} deckName={deckName} onRestart={onRestart} onBack={onBack} xpData={xpData} />;

  // Review step — shows which specific matches were right/wrong and the
  // correct garnish for each, before moving on to the aggregate results.
  if (submitted) return (
    <div className="max-w-lg mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Leaf size={16} style={{ color: C.copper }} /><span className="text-sm font-bold" style={{ color: C.copper }}>Match the Garnish — Review</span></div>
        <span className="text-xs" style={{ color: C.textMuted }}>{correctCount}/{total} correct</span>
      </div>
      <div className="space-y-2">
        {pairs.map(pair => {
          const isCorrect = getGarnishName(assignments[pair.cocktail_id]) === pair.garnish;
          return (
            <div key={pair.cocktail_id} className="px-4 py-3 rounded-lg border" style={{ borderColor: isCorrect ? "#22c55e" : "#ef4444", background: isCorrect ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)" }} data-testid={`match-review-${pair.cocktail_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-white" style={{ fontFamily: "Georgia, serif" }}>{pair.cocktail_name}</span>
                {isCorrect ? <CheckCircle2 size={16} className="text-green-400 shrink-0" /> : <XCircle size={16} className="text-red-400 shrink-0" />}
              </div>
              <p className="text-xs mt-1" style={{ color: C.textLight }}>
                You matched <span className="font-semibold">{getGarnishName(assignments[pair.cocktail_id]) || "nothing"}</span>
                {!isCorrect && <> — {pair.cocktail_name} is actually garnished with <span className="font-semibold text-green-400">{pair.garnish}</span></>}
              </p>
            </div>
          );
        })}
      </div>
      <Button onClick={() => setReviewed(true)} className="w-full h-12 font-bold" style={{ background: C.copper, color: "#000" }} data-testid="match-see-results">See Results</Button>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto space-y-4" ref={containerRef}>
      {confirmExit && <ExitConfirmDialog onStay={() => setConfirmExit(false)} onLeave={onBack} />}
      <div className="flex items-center justify-between gap-2">
        <button onClick={requestExit} className="flex items-center gap-1 text-sm shrink-0 py-2 -my-2" style={{ color: C.textMuted }} data-testid="challenge-back"><ArrowLeft size={16} /> Exit</button>
        <span className="text-xs shrink-0" style={{ color: C.textMuted }} data-testid="challenge-progress">{Object.keys(assignments).length} of {total} matched</span>
      </div>
      <div className="flex items-center justify-center gap-2">
        <Leaf size={16} style={{ color: C.copper }} /><span className="text-sm font-bold" style={{ color: C.copper }}>Match the Garnish</span>
      </div>
      <p className="text-xs text-center" style={{ color: C.textMuted }}>Tap a garnish, then tap the cocktail to assign it</p>

      {/* Garnish pool */}
      <div className="rounded-xl border p-3" style={{ borderColor: "#333", background: C.cardBg }}>
        <p className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.copper }}>Garnishes</p>
        <div className="flex flex-wrap gap-2">
          {availableGarnishes.map(g => (
            <button
              key={g.id}
              draggable
              onDragStart={() => handleDragStart(g)}
              onClick={() => handleTouchStart(g)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-grab active:cursor-grabbing min-h-[36px] ${touchDragging?.id === g.id ? 'ring-2' : ''}`}
              style={{
                borderColor: touchDragging?.id === g.id ? C.copper : "#444",
                background: touchDragging?.id === g.id ? C.copperLight : "rgba(255,255,255,0.05)",
                color: touchDragging?.id === g.id ? C.copper : "#fff",
              }}
              data-testid={`garnish-chip-${g.id}`}
            >
              {g.name}
            </button>
          ))}
          {availableGarnishes.length === 0 && <p className="text-xs" style={{ color: C.textMuted }}>All garnishes placed!</p>}
        </div>
      </div>

      {/* Cocktail slots */}
      <div className="space-y-2">
        {pairs.map(pair => {
          const assigned = assignments[pair.cocktail_id];
          return (
            <div
              key={pair.cocktail_id}
              onDragOver={e => e.preventDefault()}
              onDrop={() => handleDrop(pair.cocktail_id)}
              onClick={() => !assigned && handleTouchDrop(pair.cocktail_id)}
              className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-all min-h-[48px] ${!assigned ? 'border-dashed cursor-pointer' : ''}`}
              style={{
                borderColor: assigned ? "#444" : (touchDragging ? C.copper : "#333"),
                background: assigned ? "rgba(255,255,255,0.03)" : (touchDragging ? "rgba(220,170,44,0.05)" : "rgba(255,255,255,0.02)"),
              }}
              data-testid={`match-slot-${pair.cocktail_id}`}
            >
              <span className="text-sm font-bold text-white" style={{ fontFamily: "Georgia, serif" }}>{pair.cocktail_name}</span>
              {assigned ? (
                <button onClick={(e) => { e.stopPropagation(); removeAssignment(pair.cocktail_id); }}
                  className="px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5"
                  style={{ borderColor: "#555", background: "rgba(255,255,255,0.08)", color: C.textLight }}
                  data-testid={`match-assigned-${pair.cocktail_id}`}
                >
                  <Leaf size={12} /> {getGarnishName(assigned)} <XCircle size={12} className="ml-1 opacity-60" />
                </button>
              ) : (
                <span className="text-xs italic" style={{ color: "#555" }}>Drop garnish here</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Submit */}
      <Button onClick={handleSubmit} disabled={!allAssigned} className="w-full h-12 font-bold disabled:opacity-40"
        style={{ background: allAssigned ? C.copper : "#333", color: allAssigned ? "#000" : "#666" }} data-testid="match-submit">
        Check Answers ({Object.keys(assignments).length}/{total})
      </Button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════ */
export default function ChallengePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cocktailIds, deckName, recipeMode = "batch" } = location.state || {};

  const [mode, setMode] = useState(null); // null = picker
  const [questions, setQuestions] = useState([]);
  const [matchData, setMatchData] = useState(null);
  const [randomCocktail, setRandomCocktail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadChallenge = useCallback(async (challengeId) => {
    if (!cocktailIds?.length) { setError("No cocktails provided"); return; }
    setLoading(true);
    setError(null);
    try {
      if (challengeId === "random20") {
        const res = await axios.post(`${API}/challenge/generate`, { cocktail_ids: cocktailIds, count: 20, recipe_mode: recipeMode });
        setQuestions(res.data.questions);
      } else if (challengeId === "garnish-glass") {
        const res = await axios.post(`${API}/challenge/garnish-glass`, { cocktail_ids: cocktailIds, count: 20 });
        setQuestions(res.data.questions);
      } else if (challengeId === "match-garnish") {
        const res = await axios.post(`${API}/challenge/match-garnish`, { cocktail_ids: cocktailIds, count: 10 });
        setMatchData(res.data);
      } else if (challengeId === "random-cocktail") {
        const res = await axios.post(`${API}/challenge/random-cocktail`, { cocktail_ids: cocktailIds, recipe_mode: recipeMode });
        setRandomCocktail(res.data);
        setQuestions(res.data.questions);
      }
      setMode(challengeId);
    } catch {
      setError("Failed to load challenge");
    } finally {
      setLoading(false);
    }
  }, [cocktailIds, recipeMode]);

  const handleBack = () => {
    if (mode) { setMode(null); setQuestions([]); setMatchData(null); setRandomCocktail(null); setError(null); }
    else navigate(-1);
  };

  const handleRestart = () => {
    const currentMode = mode;
    setMode(null); setQuestions([]); setMatchData(null); setRandomCocktail(null);
    setTimeout(() => loadChallenge(currentMode), 50);
  };

  if (!cocktailIds?.length && !loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: C.pageBg }}>
      <p className="text-red-400">No cocktails provided</p>
      <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: C.pageBg }}>
      <p style={{ color: C.textMuted }}>Loading practice...</p>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: C.pageBg }}>
      <p className="text-red-400">{error}</p>
      <Button variant="outline" onClick={handleBack} data-testid="challenge-back-error">Go Back</Button>
    </div>
  );

  // ═══ PICKER ═══
  if (!mode) return (
    <div className="min-h-screen px-4 py-8" style={{ background: C.pageBg }}>
      <div className="max-w-lg mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm" style={{ color: C.textMuted }} data-testid="challenge-back"><ArrowLeft size={16} /> Back</button>
          <span className="text-sm font-bold" style={{ color: C.copper }}>{deckName}</span>
          <span />
        </div>
        <div className="text-center">
          <Zap size={36} className="mx-auto mb-2" style={{ color: C.copper }} />
          <h1 className="text-xl font-bold text-white uppercase tracking-wide" style={{ fontFamily: "Georgia, serif" }}>Choose a Practice Mode</h1>
          <p className="text-xs mt-1" style={{ color: C.textMuted }}>XP-scored — not an official Test, and doesn't change any card's Mastered status</p>
          <span
            className="inline-block mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border"
            style={{ borderColor: C.copper, color: C.copper }}
            data-testid="challenge-recipe-mode-badge"
          >
            {recipeMode === "full" ? "Full Recipe & Method" : "Batch Measurements"}
          </span>
        </div>
        <div className="space-y-3">
          {CHALLENGES.map(ch => {
            const Icon = ch.icon;
            return (
              <button
                key={ch.id}
                onClick={() => loadChallenge(ch.id)}
                className="w-full flex items-center gap-4 px-5 py-4 rounded-xl border transition-all hover:brightness-110 text-left"
                style={{ borderColor: "#333", background: C.cardBg }}
                data-testid={`challenge-pick-${ch.id}`}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: C.copperLight }}>
                  <Icon size={20} style={{ color: C.copper }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-white">{ch.label}</p>
                  <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{ch.desc}</p>
                  <p className="text-[10px] mt-1 font-semibold" style={{ color: C.copper }}>
                    {ch.maxCount ? `Up to ${ch.maxCount} ${ch.id === "match-garnish" ? "pairs" : "questions"}` : "Question count varies"}{ch.estimate ? ` · ${ch.estimate}` : ""}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ═══ MATCH MODE ═══
  if (mode === "match-garnish" && matchData) return (
    <div className="min-h-screen px-4 py-6" style={{ background: C.pageBg }}>
      <MatchMode pairs={matchData.pairs} allGarnishes={matchData.all_garnishes} deckName={deckName} onRestart={handleRestart} onBack={handleBack} />
    </div>
  );

  // ═══ QUIZ MODES (random20, garnish-glass, random-cocktail) ═══
  if (questions.length > 0) {
    const subtitle = mode === "random20" ? "Random 20" : mode === "garnish-glass" ? "Garnish & Glassware" : randomCocktail ? randomCocktail.cocktail_name : "Practice";
    return (
      <div className="min-h-screen px-4 py-6" style={{ background: C.pageBg }}>
        {mode === "random-cocktail" && randomCocktail && (
          <div className="max-w-lg mx-auto mb-4 rounded-xl border overflow-hidden" style={{ borderColor: "#333", background: C.cardBg }}>
            {randomCocktail.image_url && (
              <div className="h-32 overflow-hidden">
                <img src={`${BACKEND}${randomCocktail.image_url}`} alt={randomCocktail.cocktail_name} className="w-full h-full object-cover opacity-80" />
              </div>
            )}
            <div className="px-4 py-2 text-center" style={{ background: C.headerBg }}>
              <p className="text-xs uppercase tracking-widest font-bold" style={{ color: C.copper }}>Random Cocktail</p>
              <p className="text-lg font-bold text-white" style={{ fontFamily: "Georgia, serif" }}>{randomCocktail.cocktail_name}</p>
            </div>
          </div>
        )}
        <QuizMode questions={questions} deckName={deckName} subtitle={subtitle} challengeType={mode} onRestart={handleRestart} onBack={handleBack} />
      </div>
    );
  }

  return null;
}
